// AI Generated Component
import { Component, OnDestroy, OnInit } from '@angular/core';
import { StateService } from '@uirouter/core';
import { forkJoin, of, Subject } from 'rxjs';
import { catchError, takeUntil } from 'rxjs/operators';
import { MON_EVENTS } from '@monsido/core/constants/mon-events.constant';
import { MON_MODULES } from '@client/ng2/core/constants/mon-modules.constant';
import { PageDetailsComponent } from '@client/app/components/pagedetails/details/page-details.component';
import { CrawlsRepoService } from '@client/app/services/api/crawls-repo/crawls-repo.service';
import { SessionService } from '@monsido/core/session/session.service';
import { MonEventService } from '@monsido/services/mon-event/mon-event.service';
import { PageRepoService } from '@client/app/services/api/page-repo/page-repo.service';
import { LinksRepoService } from '@client/app/services/api/links-repo/links-repo.service';
import { PageService } from '@client/app/services/entities/page/page.service';
import { LoginService } from '@client/app/core/services/login/login.service';
import { ActiveFeatureService } from '@monsido/services/active-feature/active-feature.service';
import { GlobalHelperService } from '@client/app/services/global-helper/global-helper.service';

@Component({
    selector: 'domain-dashboard',
    standalone: false,
    template: `
        <captext
            *ngIf="domainScanNearLimit()"
            [domainTitle]="domain?.title"
            [domainItemCount]="domainItemCount"
            [maxPageCount]="customer?.plan_traits?.max_scanned_pages"
            [scanDocuments]="domain?.scan?.scan_documents">
        </captext>
        <page-header
            [header]="'Dashboard' | translate"
            [subHeader]="'Overview of all the enabled modules for this domain' | translate">
        </page-header>
        <div id="domain-dashboard" class="row justify-content-center">
            <div class="col-47 col-md-48">
                <div class="row align-items-stretch">
                    <div class="col-md-24 col-48">
                        <domain-dashboard-history
                            *ngIf="historyData"
                            [historyData]="historyData">
                        </domain-dashboard-history>
                    </div>
                    <div class="col-md-24 col-48 mt-3 mt-md-0">
                        <domain-dashboard-notifications
                            *ngIf="showNotifications"
                            [contentCount]="totalContentCount"
                            [pageCount]="pagesCount"
                            [pdfCount]="pdfsCount"
                            [documentCount]="documentsCount">
                        </domain-dashboard-notifications>
                    </div>
                </div>
                <div class="row align-items-stretch mt-3 mt-md-0 pb-5">
                    <div class="col-md-16 col-sm-24 mt-4"
                         *ngIf="totalContentCount !== null && linksCount !== null && historyData && ('global_policy' | activeFeature) && (authUser | hasAccess: modules.policies) && showPolicy">
                        <domain-dashboard-policies
                            [historyData]="historyData"
                            [totalContentCount]="totalContentCount"
                            [linksCount]="linksCount">
                        </domain-dashboard-policies>
                    </div>
                    <div class="col-md-16 col-sm-24 mt-4"
                         *ngIf="historyData && (authUser | hasAccess: modules.qa) && showQA">
                        <domain-dashboard-qa [historyData]="historyData"></domain-dashboard-qa>
                    </div>
                    <div class="col-md-16 col-sm-24 mt-4"
                         *ngIf="historyData && showAccessibility && (authUser | hasAccess: modules.accessibility)">
                        <domain-dashboard-accessibility [historyData]="historyData"></domain-dashboard-accessibility>
                    </div>
                    <div class="col-md-16 col-sm-24 mt-4"
                         *ngIf="historyData && (authUser | hasAccess: modules.seo) && showSEO">
                        <domain-dashboard-seo [historyData]="historyData"></domain-dashboard-seo>
                    </div>
                    <div class="col-md-16 col-sm-24 mt-4"
                         *ngIf="domainGroup === null && showHeartbeat && (authUser | hasAccess: modules.uptime)">
                        <domain-dashboard-heartbeat [historyData]="historyData"></domain-dashboard-heartbeat>
                    </div>

                    <div class="col-md-16 col-sm-24 mt-4"
                         *ngIf="showStatistics && domainGroup === null && (authUser | hasAccess: modules.statistics)">
                        <domain-dashboard-statistics></domain-dashboard-statistics>
                    </div>
                    <div class="col-md-16 col-sm-24 mt-4"
                         *ngIf="showDataPrivacy && domainGroup === null && (authUser | hasAccess: modules.data_privacy) && ('dataprivacy' | domainActiveFeature:domain)">
                        <domain-dashboard-data-privacy></domain-dashboard-data-privacy>
                    </div>
                </div>
            </div>
        </div>
    `,
})
export class DomainDashboardComponent implements OnInit, OnDestroy {
    domain: any = null;
    domainGroup: any = null;
    showNotifications: boolean = false;
    showPolicy: boolean = false;
    showSEO: boolean = false;
    showQA: boolean = false;
    customer: any;
    showHeartbeat: boolean = false;
    domainItemCount: number = 0;
    showAccessibility: boolean = false;
    showStatistics: boolean = false;
    showDataPrivacy: boolean = false;
    authUser: any = null;
    modules: any;
    historyData: any;
    pagesCount: number = 0;
    documentsCount: number = 0;
    pdfsCount: number = 0;
    totalContentCount: number = 0;
    linksCount: number = 0;

    private canceler$: Subject<void> = new Subject<void>();

    constructor (
        private crawlsRepoService: CrawlsRepoService,
        private sessionService: SessionService,
        private stateService: StateService,
        private monEventService: MonEventService,
        private pageRepoService: PageRepoService,
        private linksRepoService: LinksRepoService,
        private pageService: PageService,
        private coreLoginService: LoginService,
        private activeFeatureService: ActiveFeatureService,
        private globalHelperService: GlobalHelperService,
    ) {
        this.modules = MON_MODULES;
    }

    ngOnInit (): void {
        this.activate();
    }

    ngOnDestroy (): void {
        this.onDestroy();
    }

    domainScanNearLimit (): boolean {
        if (!this.customer || !this.domain) {
            return false;
        }

        return this.domainItemCount >= this.customer.plan_traits.max_scanned_pages;
    }

    private activate (): void {
        this.domain = null;
        this.domainGroup = null;
        this.showNotifications = this.activeFeatureService.isFeatureActive('dashboard_important_notifications');
        this.showPolicy = this.activeFeatureService.isFeatureActivePure('policies_all');
        this.showSEO = this.activeFeatureService.isFeatureActivePure('seo_all');
        this.showQA = this.activeFeatureService.isFeatureActivePure('qa_spellcheck') || this.activeFeatureService.isFeatureActivePure('qa_links');
        this.customer = this.sessionService.customer;
        this.showHeartbeat = this.customer !== null && this.customer.plan_traits.heartbeat;
        this.domainItemCount = 0;
        this.showAccessibility = false;
        this.showStatistics = false;
        this.showDataPrivacy = false;
        this.authUser = null;

        if (this.stateService.params.page_id) {
            this.openPage();
        }

        this.canceler$ = this.globalHelperService.getSubject();

        this.authUser = null;
        this.coreLoginService.getUser().then((user: any) => {
            this.authUser = user;
        }, () => {});

        forkJoin([
            this.getCrawls(),
            this.getPages('html'),
            this.getPages('document'),
            this.getPages('pdf'),
            this.getLinks(),
        ])
            .pipe(
                takeUntil(this.canceler$),
                catchError(() => of([])),
            )
            .subscribe({
                next: (values: any) => {
                    this.setupDomain();

                    const [crawls, htmlPages, documentPages, pdfPages, links] = values;

                    this.historyData = crawls;
                    this.pagesCount = parseInt(htmlPages?.total || 0);
                    this.documentsCount = parseInt(documentPages?.total || 0);
                    this.pdfsCount = parseInt(pdfPages?.total || 0);
                    this.totalContentCount = this.pagesCount + this.documentsCount + this.pdfsCount;
                    this.linksCount = parseInt(links?.total || 0);

                    if (this.historyData && this.historyData[0]) {
                        this.domainItemCount = this.historyData[0].page_count;

                        if (this.domain.scan.scan_documents) {
                            this.domainItemCount += this.historyData[0].documents_count;
                        }
                    }
                },
            });
    }

    private onDestroy (): void {
        this.canceler$.next();
        this.canceler$.complete();
    }

    private getCrawls (): any {
        const params = {
            page_size: 6,
        };

        return this.crawlsRepoService.getAllObservable(params, this.canceler$);
    }

    private getPages (type: string): any {
        const params = {
            page_size: 1,
            page: 1,
            'types[]': type,
        };

        return this.pageRepoService.getAllObservable(params, this.canceler$);
    }

    private getLinks (): any {
        const params = {
            page_size: 1,
            page: 1,
            page_fixed: false,
        };

        return this.linksRepoService.getAllObservable(params, this.canceler$);
    }

    private openPage (): void {
        this.pageService.get(this.stateService.params.page_id).then((page: any) => {
            const data = {
                page: page,
            };

            const params = {
                body: PageDetailsComponent,
                size: 'lg',
                classes: 'page-details-app',
                data: data,
            };

            this.monEventService.run(MON_EVENTS.LOAD_DIALOG, { params });
        }, () => {});
    }

    private setupDomain (): void {
        this.domain = this.sessionService.domain;
        this.domainGroup = this.sessionService.domainGroup;
        this.showAccessibility = this.activeFeatureService.isFeatureActive(this.modules.accessibility);
        this.showStatistics = this.activeFeatureService.isFeatureActive(this.modules.statistics);
        this.showDataPrivacy = this.activeFeatureService.isFeatureActive(this.modules.data_privacy);
        this.domainItemCount = this.domain.crawled_pages || 0;

        if (this.domainGroup === null && this.domain.scan.scan_documents) {
            let documentCount = 0;
            if (this.domain.crawl_history) {
                documentCount = parseInt(this.domain.crawl_history.documents_count, 10) || 0;
            }
            this.domainItemCount = this.domainItemCount + documentCount;
        } else if (this.domainGroup !== null) {
            this.domainItemCount = parseInt(this.domainGroup.page_count, 10) || 0;
        }
    }
}
